import React, {
  useEffect,
  useCallback,
  useMemo,
  useState,
  useRef,
} from "react";
import Button from "../Buttons/Button";
import DropdownMenu from "../Buttons/DropdownMenu";
import { useNavigate } from "react-router-dom";
import { useTabsURL } from "./NewTabWrapper";

// Constants moved outside component
const VALID_URL_MODES = ["readable", "non-readable", "encoded", "opaque"];
const CONTAINER_CLASS = "flex justify-center items-center p-1 bg-white rounded-full w-fit overflow-hidden border-2 border-gray-200";
const INNER_CONTAINER_CLASS = "flex items-center w-full";

const NewTabPill = ({
  tabs,
  mainTabsShow = 0,
  defaultTab = 0,
  onTabChange,
  mode = "basic", // basic, url
  tabId = "tabId",
}) => {
  const navigate = useNavigate();
  const tabContainerRef = useRef(null);
  const [visibleTabs, setVisibleTabs] = useState(tabs.length);
  const [activeMainTab, setActiveMainTab] = useState(defaultTab);
  const [activeSubTab, setActiveSubTab] = useState(null);
  const [isMoreMenuActive, setIsMoreMenuActive] = useState(false);

  const tabsURL = useTabsURL();

  // Memoized URL mode configuration
  const urlMode = useMemo(() => {
    const v = import.meta.env?.VITE_PAGE_TAB_URL;
    return String(v || "").toLowerCase();
  }, []);

  const writeSlug = useMemo(() => 
    VALID_URL_MODES.includes(urlMode),
    [urlMode]
  );

  // Memoized slug generation
  const makeSlug = useCallback((s) => {
    if (typeof s !== "string") return "";
    return s
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
  }, []);

  // Memoized filtered tabs
  const { mainOnlyTabs, subOnlyTabs } = useMemo(() => ({
    mainOnlyTabs: tabs.filter((t) => t.type === "main"),
    subOnlyTabs: tabs.filter((t) => t.type === "sub")
  }), [tabs]);

  // Memoized initial active tab calculation
  const initialActiveTab = useMemo(() => {
    if (mode !== "url" || !tabsURL) return defaultTab;

    const v = tabsURL.getParam(tabId);
    if (v === null || v === undefined || v === "") return defaultTab;

    const num = Number(v);
    if (!Number.isNaN(num)) return num;

    const idx = mainOnlyTabs.findIndex(
      (t) => makeSlug(t.slug ?? t.title) === String(v)
    );
    return idx >= 0 ? idx : defaultTab;
  }, [mode, tabId, tabsURL, defaultTab, mainOnlyTabs, makeSlug]);

  // Initialize active tab from URL or default
  useEffect(() => {
    setActiveMainTab(initialActiveTab);
  }, [initialActiveTab]);

  // Fixed visibility calculation - show all tabs
  const calculateVisibleTabs = useCallback(() => {
    setVisibleTabs(tabs.length);
  }, [tabs.length]);

  useEffect(() => {
    setVisibleTabs(tabs.length);
  }, [tabs.length]);

  // Set initial URL parameter if in URL mode
  useEffect(() => {
    if (mode !== "url" || !tabsURL) return;

    const current = tabsURL.getParam(tabId);
    if (current !== null && current !== undefined && current !== "") return;

    const item = mainOnlyTabs[initialActiveTab];
    const slug = item ? makeSlug(item.slug ?? item.title) : String(initialActiveTab);
    const value = writeSlug ? slug : String(initialActiveTab);
    tabsURL.setParam(tabId, value);
  }, [mode, tabsURL, tabId, initialActiveTab, mainOnlyTabs, makeSlug, writeSlug]);

  // Memoized tab finder function
  const findTabBySlug = useCallback((slug, tabList) => {
    return tabList.findIndex(
      (t) => makeSlug(t.slug ?? t.title) === slug
    );
  }, [makeSlug]);

  // Memoized URL parameter handler
  const handleURLParamChange = useCallback((value) => {
    if (value === null || value === undefined || value === "") return;

    const val = String(value);

    // Try slug-based lookup first if writeSlug is enabled
    if (writeSlug) {
      const mainIdx = findTabBySlug(val, mainOnlyTabs);
      if (mainIdx >= 0) {
        setActiveMainTab(mainIdx);
        setActiveSubTab(null);
        return;
      }

      const subIdx = subOnlyTabs.findIndex(
        (t) => t.likeMain && makeSlug(t.slug ?? t.title) === val
      );
      if (subIdx >= 0) {
        setActiveSubTab(subIdx);
        return;
      }
    }

    // Try numeric lookup
    const num = Number(val);
    if (!Number.isNaN(num)) {
      setActiveMainTab(num);
      setActiveSubTab(null);
      return;
    }

    // Fallback to slug lookup even if writeSlug is false
    const mainIdx = findTabBySlug(val, mainOnlyTabs);
    if (mainIdx >= 0) {
      setActiveMainTab(mainIdx);
      setActiveSubTab(null);
      return;
    }

    const subIdx = subOnlyTabs.findIndex(
      (t) => t.likeMain && makeSlug(t.slug ?? t.title) === val
    );
    if (subIdx >= 0) {
      setActiveSubTab(subIdx);
    }
  }, [writeSlug, mainOnlyTabs, subOnlyTabs, findTabBySlug, makeSlug]);

  // Subscribe to URL changes
  useEffect(() => {
    if (mode !== "url" || !tabsURL) return;

    tabsURL.registerKey(tabId);

    // Subscribe to changes
    const unsub = tabsURL.subscribe(tabId, handleURLParamChange);

    // Handle current value
    const current = tabsURL.getParam(tabId);
    handleURLParamChange(current);

    return () => {
      unsub();
      tabsURL.unregisterKey(tabId);
    };
  }, [mode, tabId, tabsURL, handleURLParamChange]);

  // Memoized tab distribution
  const { mainTabs, mainMore, subTabs, showMore } = useMemo(() => {
    const effectiveTabCount =
      mainTabsShow === 0 ? visibleTabs : Math.min(mainTabsShow, visibleTabs);
    const mainTabs = mainOnlyTabs.slice(0, effectiveTabCount);
    const mainMore = mainOnlyTabs.slice(effectiveTabCount);

    const activeMainTabData = mainOnlyTabs[activeMainTab];
    const subTabs = activeMainTabData?.subTabs || subOnlyTabs || [];

    const showMore = mainMore.length > 0;

    return { mainTabs, mainMore, subTabs, showMore };
  }, [mainOnlyTabs, subOnlyTabs, mainTabsShow, visibleTabs, activeMainTab]);

  // Memoized tab click handler
  const handleTabClick = useCallback(
    (item, index, isMainTab = true) => {
      if (isMainTab) {
        if (mode === "url" && tabsURL) {
          const slug = makeSlug(item?.slug ?? item.title);
          const value = writeSlug ? slug : String(index);
          tabsURL.setParam(tabId, value);
        }
        setActiveMainTab(index);
        setActiveSubTab(null);
        onTabChange?.(index);
      } else {
        if (item.likeMain && mode === "url" && tabsURL) {
          const slug = makeSlug(item?.slug ?? item.title);
          tabsURL.setParam(tabId, slug);
        }
        setActiveSubTab(index);
      }

      // Execute actions
      item.action?.();
      item.doubleAction?.();
      
      if (item.link) {
        navigate(item.link);
      }
    },
    [navigate, onTabChange, mode, tabsURL, tabId, writeSlug, makeSlug]
  );

  // Update "More" menu active state
  useEffect(() => {
    const isInMoreMenu = activeMainTab >= mainTabs.length;
    setIsMoreMenuActive(isInMoreMenu);
  }, [activeMainTab, mainTabs.length]);

  // Memoized setActiveTab handler for child pages
  const createSetActiveTabHandler = useCallback(() => {
    return (next) => {
      const nextIndex = typeof next === "function" ? next(activeMainTab) : next;
      const safeIndex = Math.max(
        0,
        Math.min(mainOnlyTabs.length - 1, Number(nextIndex) || 0)
      );

      if (mode === "url" && tabsURL) {
        const item = mainOnlyTabs[safeIndex];
        const slug = item ? makeSlug(item.slug ?? item.title) : String(safeIndex);
        const value = writeSlug ? slug : String(safeIndex);
        tabsURL.setParam(tabId, value);
      }

      setActiveMainTab(safeIndex);
      setActiveSubTab(null);
      onTabChange?.(safeIndex);
    };
  }, [activeMainTab, mainOnlyTabs, mode, tabsURL, tabId, writeSlug, makeSlug, onTabChange]);

  // Memoized button class generator
  const getButtonClass = useCallback((index) => {
    const isActive = index === activeMainTab;
    const isFirst = index === 0;
    const isLast = index === mainTabs.length - 1;
    
    const roundClass = isFirst 
      ? "!rounded-l-full !rounded-r-none" 
      : isLast 
        ? "!rounded-r-full !rounded-l-none" 
        : "!rounded-none";

    const stateClass = isActive
      ? "!py-2 transition-all duration-300 !bg-orange-500 !text-white !border-orange-500 shadow-md whitespace-nowrap"
      : "!py-2 transition-all duration-300 !bg-orange-50 !text-black !border-transparent hover:!bg-orange-100 whitespace-nowrap";

    return `main-tab-button w-32 ${roundClass} ${stateClass}`;
  }, [activeMainTab, mainTabs.length]);

  // Memoized "More" dropdown class
  const moreDropdownClass = useMemo(() => {
    return `cursor-pointer rounded-full !px-6 !py-2 transition-all duration-300 ${
      isMoreMenuActive
        ? "!bg-orange-500 !text-white !border-orange-500 shadow-md"
        : "!bg-transparent !text-gray-500 hover:!bg-orange-100"
    }`;
  }, [isMoreMenuActive]);

  // Memoized current page content
  const currentPageContent = useMemo(() => {
    const isSubTabActive = activeSubTab !== null && subTabs[activeSubTab]?.likeMain;
    const currentPage = isSubTabActive 
      ? subTabs[activeSubTab]?.page 
      : mainOnlyTabs[activeMainTab]?.page;

    if (!currentPage) return null;

    // Clone element with setActiveTab prop
    return React.cloneElement(currentPage, {
      setActiveTab: createSetActiveTabHandler()
    });
  }, [activeSubTab, subTabs, activeMainTab, mainOnlyTabs, createSetActiveTabHandler]);

  return (
    <div
      role="tablist"
      className="tab-container flex flex-1 flex-col overflow-hidden items-center w-full"
    >
      <div className="flex items-center w-full gap-4">
        <div className="h-[2px] bg-gray-200 flex-1 rounded-full"></div>
        
        <div className={CONTAINER_CLASS}>
          <div ref={tabContainerRef} className={INNER_CONTAINER_CLASS}>
            {mainTabs.map((item, key) => (
              <Button
                key={key}
                className={getButtonClass(key)}
                type="tab"
                role="tab"
                aria-selected={key === activeMainTab}
                color="white"
                variant="text"
                title={item.title}
                icon={item.icon}
                iconPosition={item.iconPosition}
                size="sm"
                onClick={() => handleTabClick(item, key)}
                active={key === activeMainTab}
                disabled={item.disabled}
                value={item.value}
              />
            ))}

            {showMore && (
              <DropdownMenu
                MenuSection={
                  <div className="flex flex-col py-1">
                    {mainMore.map((item, index) => (
                      <button
                        key={index}
                        className="px-4 py-2 text-left hover:bg-orange-50 text-gray-700 text-sm transition-colors"
                        onClick={() => handleTabClick(item, mainTabs.length + index)}
                      >
                        {item.title}
                      </button>
                    ))}
                  </div>
                }
              >
                <div className={moreDropdownClass}>
                  More
                </div>
              </DropdownMenu>
            )}
          </div>
        </div>
        
        <div className="h-[2px] bg-gray-200 flex-1 rounded-full"></div>
      </div>

      <div
        role="tabpanel"
        className="tab-content flex flex-1 overflow-hidden w-full"
      >
        {currentPageContent && (
          <div className="flex flex-1 flex-col overflow-auto">
            {currentPageContent}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(NewTabPill);
